// Assemblies built from components (db/88 EstimateCostAssemblyComponents). Pure functions, no DB.
//
// An assembly may list catalog Items and other Assemblies. A component's quantity is one line of text: a plain number N
// (× the takeoff quantity) or a formula. A formula sees the parent's quantity as `qty` (in the assembly's own UOM: what
// the estimator takes off), the Engine Variables and the library formulas by code, and is evaluated on the line's TOTAL
// quantity, never per unit (ceil(qty / 10) is not linear). This module does
// the three things the Estimate needs, always live and never stored:
//   1. flattenAssembly  — walks nested assemblies down to leaves (an Item, or an Assembly that has no
//      components of its own and so still prices flat), working out each leaf's quantity for a given
//      parent quantity; a nested assembly's computed quantity is the `qty` its own formulas see.
//   2. applyAssemblyRollups — gives an assembly with components rolled-up material / labor-hours /
//      vendor cost per unit, so every flat consumer of the assembly list (manual lines, room
//      elements, scope) prices it from its components instead of its own typed numbers.
//   3. explodeRollupRows — replaces an assembly's Estimate line with one line per cost code its
//      components sit under (or one line when the assembly's ComponentDisplay is 'single_line').
//      Each component is priced with ITS OWN pricing rules (burden, markup, rounding) at its own
//      unit cost, exactly as it would be priced if it stood alone — the assembly's own sell settings
//      are ignored once it has components.
// A loop, a missing child, a formula that cannot be calculated or a nesting that is too deep makes the
// whole assembly "not ok": callers keep the flat line and show the warning, so a bad catalog edit never
// silently drops money (and a failed formula is never read as zero).

import { roundForPricing } from "./quantityRounding";
import { computeUnitSellPrice } from "./estimatePricing";
import { evaluateFormula } from "./formulaEngine";
import { applyRollupOverride } from "./rollupOverrides";

export const DISPLAY_MODES = { byCostCode: "by_cost_code", singleLine: "single_line" };
export const MAX_NESTING_DEPTH = 8;

// Estimator edits to a component's line on the Estimate (db/47 EstimateAssemblyLineOverrides): keyed by the assembly, the
// component's catalog Item / Assembly, the alternate and add / credit — never by a component row (saving an assembly replaces them).
export const childIdOf = (leaf) => (leaf.kind === "item" ? leaf.entity.ItemID : leaf.entity.AssemblyID);
export const leafOverrideKey = (assemblyId, kind, childId, alternateId, isCredit) => `${assemblyId}:${kind}:${childId}:${alternateId ?? 0}:${isCredit ? "cr" : "add"}`;
export const leafOverrideKeyOfRow = (o) => leafOverrideKey(o.AssemblyID, o.ChildKind, o.ChildID, o.AlternateID, o.IsCredit);

// rows (sp_GetEstimateAssemblyComponents) -> Map<AssemblyID, rows in display order>
export function indexComponents(rows) {
  const map = new Map();
  for (const r of rows || []) (map.get(r.AssemblyID) || map.set(r.AssemblyID, []).get(r.AssemblyID)).push(r);
  for (const list of map.values()) list.sort((a, b) => (a.DisplayOrder - b.DisplayOrder) || (a.ComponentID - b.ComponentID));
  return map;
}

// A component's quantity is ONE line of text. A plain number N means "the takeoff quantity × N" (type 4 for × 4); anything
// else is a formula, where `qty` is the assembly's own takeoff quantity (in the assembly's UOM), Engine Variables are
// available by name, and so is any Estimate Engine library formula BY ITS CODE (`PULLBOX * 4`). Blank = one per unit.
const PLAIN_NUMBER_RE = /^\s*(\d+\.?\d*|\.\d+)\s*$/;
export const isPlainNumber = (text) => PLAIN_NUMBER_RE.test(String(text ?? ""));
export function quantityExpression(text) {
  const t = String(text ?? "").trim();
  if (!t) return "qty";
  return isPlainNumber(t) ? `qty * ${t}` : t;
}

// The library formulas that can be used by code inside a component formula, evaluated at this qty: each one is calculated
// with ONLY `qty` and the Engine Variables, so a library formula that needs room or circuit variables is simply not offered.
export function libraryFormulaValues(libraryFormulas, qty, engineVars) {
  const out = {};
  for (const f of libraryFormulas || []) {
    if (!f?.Code || f.IsActive === false || f.IsActive === 0 || !/^[A-Za-z_][A-Za-z0-9_]*$/.test(f.Code)) continue;
    try {
      const v = evaluateFormula(f.Expression, { ...(engineVars || {}), qty });
      if (Number.isFinite(v)) out[f.Code] = v;
    } catch { /* needs variables an assembly does not have — not available here */ }
  }
  return out;
}

// One component's quantity for a given parent quantity: { qty, basis: 'formula' | 'multiplier', formula } or { error: { code, message } }.
// ctx.engineVars = Engine Variable values; ctx.libraryFormulas = the library rows ({ Code, Expression, IsActive }). A formula that
// cannot be calculated is an ERROR — never zero. A row with no QtyFormula is the original quantity-per-unit model.
export function componentQuantity(c, parentQty, ctx) {
  const text = String(c.QtyFormula ?? "").trim();
  if (!text) return { qty: parentQty * (Number(c.QtyPerUnit) || 0), basis: "multiplier", formula: null };
  const plain = isPlainNumber(text);
  try {
    const vars = { ...libraryFormulaValues(ctx.libraryFormulas, parentQty, ctx.engineVars), ...(ctx.engineVars || {}), qty: parentQty };
    const v = evaluateFormula(quantityExpression(text), vars);
    if (!Number.isFinite(v)) throw new Error("the result is not a number");
    return { qty: v, basis: plain ? "multiplier" : "formula", formula: plain ? null : text };
  } catch (e) {
    return { error: { code: "formula_error", message: `Formula "${text}" could not be calculated: ${String(e.message || e).replace(/ in formula.*$/, "")}.` } };
  }
}

const nameOf = (leaf) => (leaf.kind === "item" ? leaf.entity.ItemName : leaf.entity.Description) || leaf.entity.Code || "—";

// ctx = { componentsByAssembly, itemsById, assembliesById, engineVars? }; rootQty = the root assembly's quantity (default 1)
// -> { ok, usesFormula, leaves: [{ kind, entity, qty (absolute, for rootQty), basis, formula, path: [parent descriptions] }],
//      warnings: [{ code, message }] }
export function flattenAssembly(assemblyId, ctx, rootQty = 1) {
  const { componentsByAssembly, itemsById, assembliesById } = ctx;
  const leaves = [];
  const warnings = [];
  let usesFormula = false;
  // Returns this assembly's child nodes (the same nesting the estimator built): a leaf node IS the leaf object, a
  // sub-assembly with components of its own is { branch: true, …, children }.
  const walk = (asmId, parentQty, path, stack) => {
    const nodes = [];
    if (stack.length >= MAX_NESTING_DEPTH) { warnings.push({ code: "too_deep", message: `Assemblies are nested more than ${MAX_NESTING_DEPTH} levels deep.` }); return nodes; }
    const asm = assembliesById.get(asmId);
    const here = [...path, asm?.Description || asm?.Code || `#${asmId}`];
    for (const c of componentsByAssembly.get(asmId) || []) {
      const cq = componentQuantity(c, parentQty, ctx);
      if (cq.error) { warnings.push(cq.error); continue; }
      if (cq.basis !== "multiplier") usesFormula = true;
      const qty = cq.qty;
      if (c.ChildKind === "item") {
        const item = itemsById.get(c.ChildItemID);
        if (!item) { warnings.push({ code: "missing_child", message: `A component Item (#${c.ChildItemID}) is missing or inactive.` }); continue; }
        const leaf = { kind: "item", entity: item, qty, basis: cq.basis, formula: cq.formula, path: here };
        leaves.push(leaf); nodes.push(leaf);
      } else {
        if (stack.includes(c.ChildAssemblyID) || c.ChildAssemblyID === asmId) { warnings.push({ code: "cycle", message: "An assembly contains itself." }); continue; }
        const child = assembliesById.get(c.ChildAssemblyID);
        if (!child) { warnings.push({ code: "missing_child", message: `A component Assembly (#${c.ChildAssemblyID}) is missing or inactive.` }); continue; }
        if (componentsByAssembly.has(c.ChildAssemblyID)) {
          nodes.push({ branch: true, kind: "assembly", entity: child, qty, basis: cq.basis, formula: cq.formula, children: walk(c.ChildAssemblyID, qty, here, [...stack, asmId]) });
        } else {
          const leaf = { kind: "assembly", entity: child, qty, basis: cq.basis, formula: cq.formula, path: here };
          leaves.push(leaf); nodes.push(leaf);
        }
      }
    }
    return nodes;
  };
  const tree = walk(assemblyId, rootQty, [], []);
  if (!leaves.length && !warnings.length) warnings.push({ code: "empty", message: "The assembly has no components." });
  return { ok: warnings.length === 0, usesFormula, leaves, tree, warnings };
}

// The drill-down for ONE Estimate line: the estimator's own nesting (sub-assemblies and Items), pruned to the components that
// sit in this line's cost code, with each branch's cost and price summed from what is under it. `partOf(leaf)` returns the
// priced part. Detail only — the line itself stays the rolled-up figure a client report shows.
function drillDown(nodes, inLine, partOf) {
  const out = [];
  for (const n of nodes) {
    if (n.branch) {
      const children = drillDown(n.children, inLine, partOf);
      if (!children.length) continue;
      out.push({
        kind: "assembly", branch: true, code: n.entity.Code, name: n.entity.Description || n.entity.Code, uom: n.entity.UOM,
        qty: n.qty, basis: n.basis, formula: n.formula, unitCost: null,
        direct: children.reduce((t, c) => t + c.direct, 0), sell: children.reduce((t, c) => t + c.sell, 0), children,
      });
    } else if (inLine.has(n)) {
      const p = partOf(n);
      out.push({ kind: n.kind, branch: false, code: p.code, name: p.name, uom: p.uom, qty: p.qty, basis: n.basis, formula: n.formula, unitCost: p.unitCost, direct: p.direct, sell: p.sell, children: [],
        childKind: p.childKind, childId: p.childId, overridden: p.overridden, hasRates: p.hasRates, burdenPct: p.burdenPct, markupPct: p.markupPct, unitPrice: p.qty ? p.sell / p.qty : null });
    }
  }
  return out;
}

const factorOf = (leaf, laborFactorsById) =>
  leaf.kind === "assembly" ? (laborFactorsById?.get(leaf.entity.DefaultLaborFactorID)?.Multiplier ?? 1) : 1;

// Rolled-up cost per ONE unit of the assembly (evaluated at a quantity of 1), or null when it is not ok. Exact only while every
// component is a plain quantity-per-unit; a formula component (ceil, a fixed amount…) makes it an approximation, flagged as
// `usesFormula` — the Estimate's takeoff lines never use it (they split from the line's real quantity), only flat consumers do.
export function rolledAssemblyCosts(assemblyId, ctx, laborFactorsById) {
  const flat = flattenAssembly(assemblyId, ctx, 1);
  if (!flat.ok) return null;
  let material = 0, hours = 0, vendor = 0;
  for (const l of flat.leaves) {
    material += l.qty * (Number(l.entity.MaterialCostPerUnit) || 0);
    hours += l.qty * (Number(l.entity.LaborHrPerUnit) || 0) * factorOf(l, laborFactorsById);
    vendor += l.qty * (Number(l.entity.VendorSubCostPerUnit) || 0);
  }
  return { MaterialCostPerUnit: material, LaborHrPerUnit: hours, VendorSubCostPerUnit: vendor, usesFormula: flat.usesFormula };
}

// The assembly list with every component-built assembly carrying its rolled costs. The rolled hours
// already include each nested assembly's own labor factor, so the parent's default factor is cleared
// (a per-line factor on a manual line still applies). Assemblies with no components are returned as-is.
export function applyAssemblyRollups(assemblies, { componentsByAssembly, itemsById, engineVars, libraryFormulas }, laborFactorsById) {
  if (!componentsByAssembly?.size) return assemblies;
  const assembliesById = new Map(assemblies.map(a => [a.AssemblyID, a]));
  const ctx = { componentsByAssembly, itemsById, assembliesById, engineVars, libraryFormulas };
  return assemblies.map(a => {
    if (!componentsByAssembly.has(a.AssemblyID)) return a;
    const rolled = rolledAssemblyCosts(a.AssemblyID, ctx, laborFactorsById);
    if (!rolled) return { ...a, HasComponents: true, ComponentWarnings: flattenAssembly(a.AssemblyID, ctx, 1).warnings };
    const { usesFormula, ...costs } = rolled;
    return { ...a, ...costs, DefaultLaborFactorID: null, HasComponents: true, HasFormulaComponents: usesFormula };
  });
}

const money = (n) => (+n).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtQty = (n) => (+n).toLocaleString(undefined, { maximumFractionDigits: 4 });
const codeLabel = (e) => (e?.CSICode ? `${e.CSICode}${e.CostCodeDescription ? ` ${e.CostCodeDescription}` : ""}` : "No cost code");

// One assembly priced for `qty` units, as Estimate lines. Returns { ok, lines, warnings }.
// overrideOf(leaf) (optional) returns the estimator's edit for that component, or null.
export function explodeAssemblyLine({ assembly, qty, ctx, settings, laborFactorsById, overrideOf }) {
  const flat = flattenAssembly(assembly.AssemblyID, ctx, qty);
  if (!flat.ok) return { ok: false, lines: [], warnings: flat.warnings };
  const laborRate = Number(settings?.LaborRate) || 0;
  const single = assembly.ComponentDisplay === DISPLAY_MODES.singleLine;

  const groups = new Map();
  const partByLeaf = new Map();
  for (const leaf of flat.leaves) {
    const e = leaf.entity;
    const unit = (Number(e.MaterialCostPerUnit) || 0)
      + (Number(e.LaborHrPerUnit) || 0) * factorOf(leaf, laborFactorsById) * laborRate
      + (Number(e.VendorSubCostPerUnit) || 0);
    // already worked out for THIS line's quantity (formulas run on the total, not per unit); LF / SF is whole before pricing
    const leafQty = roundForPricing(leaf.qty, e.UOM);
    // the component priced by its own rules, then the estimator's edit (qty / unit cost / burden / markup / unit price) on top —
    // the same re-pricing an Item's line gets (applyRollupOverride), so an edited component and an edited Item behave alike
    const base = { TotalQty: leafQty, DirectCost: leafQty * unit, SellValue: leafQty * computeUnitSellPrice(unit, e, settings) };
    const ov = overrideOf ? overrideOf(leaf) || null : null;
    const priced = ov ? applyRollupOverride(base, ov, e, settings) : base;
    const hasRates = e.SalesBurdenPct != null || e.MarkupPct != null || ov?.SalesBurdenPct != null || ov?.MarkupPct != null || ov?.UnitPriceOverride != null;
    const part = {
      code: e.Code, name: nameOf(leaf), uom: e.UOM, entity: e, path: leaf.path,
      basis: leaf.basis, formula: leaf.formula,
      qtyPerParentUnit: qty ? leaf.qty / qty : null, qty: priced.TotalQty, unitCost: priced.UnitCostEff ?? unit,
      direct: priced.DirectCost, sell: priced.SellValue,
      childKind: leaf.kind, childId: childIdOf(leaf), override: ov, overridden: priced.overridden || {},
      hasRates, burdenPct: hasRates ? (Number(ov?.SalesBurdenPct ?? e.SalesBurdenPct) || 0) : null,
      markupPct: hasRates ? (priced.DerivedMarkupPct != null ? priced.DerivedMarkupPct : Number(ov?.MarkupPct ?? e.MarkupPct) || 0) : null,
    };
    partByLeaf.set(leaf, part);
    const key = single ? "all" : String(e.CostCodeID ?? "none");
    (groups.get(key) || groups.set(key, { key, parts: [], leaves: new Set() }).get(key)).parts.push(part);
    groups.get(key).leaves.add(leaf);
  }

  const lines = [];
  for (const g of groups.values()) {
    const direct = g.parts.reduce((t, p) => t + p.direct, 0);
    const sell = g.parts.reduce((t, p) => t + p.sell, 0);
    // The cost code a line shows: its group's own code; for a single-line assembly, the code carrying the most cost.
    const codeSource = single
      ? [...g.parts].sort((a, b) => (b.entity.CostCodeID != null) - (a.entity.CostCodeID != null) || b.direct - a.direct)[0].entity
      : g.parts[0].entity;
    const oneLeaf = g.parts.length === 1;
    lines.push({
      EntityKind: "assembly_part", EntityID: `${assembly.AssemblyID}:${g.key}`,
      ParentKind: "assembly", ParentID: assembly.AssemblyID,
      Code: oneLeaf ? g.parts[0].code : assembly.Code,
      Description: oneLeaf ? `${assembly.Description} — ${g.parts[0].name}`
        : single ? assembly.Description : `${assembly.Description} — ${codeLabel(codeSource)}`,
      UOM: oneLeaf ? g.parts[0].uom : assembly.UOM,
      TotalQty: oneLeaf ? g.parts[0].qty : qty,
      DirectCost: direct, SellValue: sell,
      CSICode: codeSource.CSICode ?? null, CostCodeDescription: codeSource.CostCodeDescription ?? null,
      CategoryID: assembly.CategoryID ?? null,
      Exploded: true,
      // a line made of ONE component is edited like an Item's line (its edit is that component's); a multi-component line is edited
      // component by component in the drill-down
      LeafEdit: oneLeaf ? { assemblyId: assembly.AssemblyID, childKind: g.parts[0].childKind, childId: g.parts[0].childId } : null,
      LeafOverride: oneLeaf ? g.parts[0].override : null,
      LeafOverridden: oneLeaf ? g.parts[0].overridden : {},
      AnyLeafOverridden: g.parts.some(p => Object.values(p.overridden || {}).some(Boolean)),
      Components: g.parts,
      // Drill-down detail: the estimator's own nesting (sub-assemblies, Items), pruned to this line's cost code. A client
      // report shows the line itself (rolled up) and never this.
      Tree: drillDown(flat.tree, g.leaves, (leaf) => partByLeaf.get(leaf)),
      Contributions: [],
    });
  }
  return { ok: true, lines, warnings: [] };
}

// rows = the Estimate's takeoff rollup rows (after the drawn + element merge). Any row for an assembly
// that has components becomes its per-cost-code lines; every other row passes through untouched.
// qtyOverrideOf(row) (optional) lets a quantity the estimator typed over the line still drive the explosion.
// engineVars = Engine Variable values, libraryFormulas = library rows ({ Code, Expression }), both usable by name in a component formula.
// leafOverrides = Map(leafOverrideKeyOfRow(row) -> override row) of the estimator's edits to individual component lines.
export function explodeRollupRows(rows, { componentsByAssembly, itemsById, assembliesById, settings, laborFactorsById, qtyOverrideOf, engineVars, libraryFormulas, leafOverrides }) {
  const warnings = [];
  if (!componentsByAssembly?.size) return { rows, warnings };
  const ctx = { componentsByAssembly, itemsById, assembliesById, engineVars, libraryFormulas };
  const out = [];
  for (const row of rows) {
    const assembly = row.EntityKind === "assembly" && componentsByAssembly.has(row.EntityID) ? assembliesById.get(row.EntityID) : null;
    if (!assembly) { out.push(row); continue; }
    const qty = qtyOverrideOf?.(row) ?? row.TotalQty;
    if (!Number.isFinite(+qty) || +qty === 0) { out.push(row); continue; }
    const overrideOf = leafOverrides?.size ? (leaf) => leafOverrides.get(leafOverrideKey(assembly.AssemblyID, leaf.kind, childIdOf(leaf), row.AlternateID, +qty < 0)) || null : undefined;
    const res = explodeAssemblyLine({ assembly, qty: +qty, ctx, settings, laborFactorsById, overrideOf });
    if (!res.ok) {
      warnings.push(...res.warnings.map(w => ({ ...w, assemblyId: assembly.AssemblyID, assemblyCode: assembly.Code })));
      out.push({ ...row, ComponentWarnings: res.warnings });
      continue;
    }
    for (const line of res.lines) {
      out.push({
        ...row, ...line,
        MatchedInstanceCount: row.MatchedInstanceCount,
        ParentQty: +qty, ParentKey: `assembly:${assembly.AssemblyID}`,
        Contributions: [...line.Contributions, ...(row.Contributions || [])],
      });
    }
  }
  return { rows: out, warnings };
}
